import { Button, Link, Text, VStack } from '@chakra-ui/react'
import { PaymentFooter } from 'commons/components/modal/PaymentFooter'
import { PaymentSecurityInfo } from 'commons/components/modal/PaymentSecurityInfo'
import { VerifyIdentityFields } from 'commons/components/modal/VerifyIdentityFields'
import type { PaymentTypes } from 'commons/constants/paymentTypes'
import { BgColors, TextColors } from 'commons/design-system/tokens'
import { type FormEvent, type ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FormStep } from 'commons/types/paymentTypes'

type VerifyIdentityStageProps = {
	firstname: string
	lastname: string
	birthdate: string
	city?: string
	zipcode?: string
	country?: string
	step: FormStep
	errors: {
		firstname?: string
		lastname?: string
		birthdate?: string
		zipcode?: string
		city?: string
		country?: string
		generic?: string
	}
	isLoading: boolean
	onSubmit: (e: FormEvent) => void
	onFirstnameChange: (value: string) => void
	onLastnameChange: (value: string) => void
	onBirthdateChange: (value: string) => void
	onCityChange: (value: string) => void
	onCountryChange: (value: string) => void
	onZipcodeChange: (value: string) => void
	onClearError: (
		field: 'firstname' | 'lastname' | 'birthdate' | 'zipcode' | 'city' | 'country'
	) => void
	isManualMode?: boolean
	onManualModeChange?: (isManual: boolean) => void
	componentDisplayedOnSuccess?: ReactNode
	source: string
	paymentType: PaymentTypes
	hideZipAndCity?: boolean
}

export const VerifyIdentityStage = ({
	firstname,
	lastname,
	birthdate,
	city,
	zipcode,
	country,
	step,
	errors,
	isLoading,
	onSubmit,
	onFirstnameChange,
	onLastnameChange,
	onBirthdateChange,
	onCityChange,
	onCountryChange,
	onZipcodeChange,
	onClearError,
	isManualMode,
	onManualModeChange,
	componentDisplayedOnSuccess,
	source,
	paymentType,
	hideZipAndCity
}: VerifyIdentityStageProps) => {
	const { t, i18n } = useTranslation()

	const informationNoticeUrl = `https://support.lydia.me/l/${i18n.language}/article/d7b3jbxvdz`
	const tcsUrl = `https://support.lydia.me/l/${i18n.language}/article/q85z5imn58`

	const content = (
		<VStack align='stretch'>
			<VerifyIdentityFields
				firstname={firstname}
				lastname={lastname}
				birthdate={birthdate}
				city={city}
				zipcode={zipcode}
				country={country}
				step={step}
				errors={errors}
				isLoading={isLoading}
				isReadOnly={!!componentDisplayedOnSuccess}
				onFirstnameChange={onFirstnameChange}
				onLastnameChange={onLastnameChange}
				onBirthdateChange={onBirthdateChange}
				onCityChange={onCityChange}
				onCountryChange={onCountryChange}
				onZipcodeChange={onZipcodeChange}
				onClearError={onClearError}
				isManualMode={isManualMode}
				onManualModeChange={onManualModeChange}
				paymentType={paymentType}
				source={source}
				hideZipAndCity={hideZipAndCity}
			/>
			{componentDisplayedOnSuccess ? (
				componentDisplayedOnSuccess
			) : (
				<>
					{errors.generic && (
						<Text color={TextColors.STATUS_ERROR} fontSize='sm'>
							{errors.generic}
						</Text>
					)}
					<Text fontSize='sm' color={'#565656'}>
						<Trans
							i18nKey='phone_form.verify_identity_info_links'
							components={{
								infoLink: (
									<Link
										href={informationNoticeUrl}
										target='_blank'
										color={'#565656'}
										textDecoration='underline'
									/>
								),
								privacyLink: (
									<Link
										href={tcsUrl}
										target='_blank'
										color={'#565656'}
										textDecoration='underline'
									/>
								)
							}}
						/>
					</Text>
					<Button
						type='submit'
						bg={BgColors.OPPOSITE_Z3}
						color={BgColors.Z5}
						borderRadius='md'
						py={6}
						fontSize='md'
						fontWeight='bold'
						loading={isLoading}
						disabled={isLoading}
						_hover={{ bg: BgColors.OPPOSITE_Z3, opacity: 0.9 }}
						_focus={{ outline: 'none' }}
					>
						{t('phone_form.verify_identity_next_button')}
					</Button>
				</>
			)}
			<PaymentSecurityInfo source={source} />
			<PaymentFooter source={source} />
		</VStack>
	)

	return componentDisplayedOnSuccess ? content : <form onSubmit={onSubmit}>{content}</form>
}
